/**
 * Direct storefront price gates. There are three, and they are NOT interchangeable:
 *
 *   canReadPrices                catalog, product detail, cart/checkout.
 *                                can_read_prices()  =  !hide_prices-direct || read_prices
 *
 *   canReadDocumentPrices        document history listings, ageing.
 *                                can_read_document_prices()  =  read_document_prices
 *                                (the Hide Prices setting is deliberately not consulted)
 *
 *   canReadDocumentDetailPrices  document detail pages.
 *                                can_read_document_detail_prices()  =  both of the above
 *
 * All are resolved server-side and published on window.bo in frontend/layout/master.blade.php.
 * Detail pages additionally get a per-document window.bo.canReadDocumentPdf from their controller
 * (read_document_pdfs, plus read_document_prices when the document is price-bearing).
 *
 * This is a presentation gate only — amounts stay in the API payload because the client is
 * authoritative for cart pricing, so stripping them would zero out placed orders.
 */

/**
 * Catalog / product / cart gate.
 *
 * @param {Object} bo The window.bo boot object.
 * @returns {Boolean} Whether amounts may be rendered. Fails open when the key is absent, so
 *     a page that predates the gate keeps its current behaviour.
 */
export function resolveCanReadPrices(bo) {
    return bo?.canReadPrices !== false;
}

/**
 * Document history / document detail / ageing gate. Backed by read_document_prices, which is
 * a separate capability from read_prices and is not affected by the Hide Prices setting.
 *
 * @param {Object} bo The window.bo boot object.
 * @returns {Boolean}
 */
export function resolveCanReadDocumentPrices(bo) {
    return bo?.canReadDocumentPrices !== false;
}

/**
 * Document detail gate: read_document_prices AND catalog prices visible.
 *
 * @param {Object} bo The window.bo boot object.
 * @returns {Boolean}
 */
export function resolveCanReadDocumentDetailPrices(bo) {
    return bo?.canReadDocumentDetailPrices !== false;
}

/**
 * Document PDF gate for the current detail page. Published per page by the detail controller
 * (see can_read_document_pdf()), so it already accounts for whether the document bears prices.
 *
 * @param {Object} bo The window.bo boot object.
 * @returns {Boolean}
 */
export function resolveCanReadDocumentPdf(bo) {
    return bo?.canReadDocumentPdf !== false;
}

export const priceVisibilityMixin = {
    data() {
        return {
            canReadPrices: resolveCanReadPrices(window.bo),
        };
    },
};

export const documentPriceVisibilityMixin = {
    data() {
        return {
            canReadDocumentPrices: resolveCanReadDocumentPrices(window.bo),
        };
    },
};

export const documentDetailPriceVisibilityMixin = {
    data() {
        return {
            canReadDocumentDetailPrices: resolveCanReadDocumentDetailPrices(window.bo),
            canReadDocumentPdf: resolveCanReadDocumentPdf(window.bo),
        };
    },
};

/**
 * Money-bearing column ids used across the history/document datatables. Tax rate and tax
 * class names are kept — they name a rule, they do not disclose an amount.
 */
export const PRICE_COLUMN_IDS = [
    "amount",
    "discount",
    "discount_display",
    "effective_unit_price",
    "outstanding",
    // Payment detail: amount paid against an invoice / credit-note amount consumed.
    "paid",
    "payment_amount",
    "price",
    "price_before_discount",
    "subtotal",
    "total",
    "total_payable_amount",
    "unapplied_amount",
    "unit_price",
    "unit_price_after_discount",
    "used",
];

/**
 * Removes the hidden ids from a column collection, preserving its shape.
 *
 * Call sites pass either the raw array of column definitions or the object that
 * boDatatableProcessColumns() returns (keyed by column id), so both are handled — an array
 * put through Array.prototype.filter and an object rebuilt without the hidden keys.
 *
 * @param {Array|Object} columns
 * @param {Array} hidden Column ids to drop.
 * @returns {Array|Object} The same shape that was passed in.
 */
function dropColumns(columns, hidden) {
    if (Array.isArray(columns)) {
        return columns.filter((column) => !hidden.includes(column?.id));
    }

    if (columns && typeof columns === "object") {
        const kept = {};

        Object.keys(columns).forEach((key) => {
            if (!hidden.includes(columns[key]?.id ?? key)) {
                kept[key] = columns[key];
            }
        });

        return kept;
    }

    return columns;
}

/**
 * Drops money columns from a bo-datatable column definition when document prices are hidden.
 * Removing them outright (rather than setting visible:false) keeps them out of the
 * column-preference picker too.
 *
 * @param {Array|Object} columns bo-datatable column definitions.
 * @param {Array} [extraIds] Additional column ids to treat as money for this table.
 * @returns {Array|Object}
 */
export function filterPriceColumns(columns, extraIds = []) {
    // Only history listing datatables use this, so it reads the DOCUMENT gate.
    if (resolveCanReadDocumentPrices(window.bo)) {
        return columns;
    }

    return dropColumns(columns, PRICE_COLUMN_IDS.concat(extraIds));
}

/**
 * Same as filterPriceColumns(), but for the datatables on a document detail page, which are
 * gated by read_document_prices AND catalog price visibility.
 *
 * @param {Array|Object} columns bo-datatable column definitions.
 * @param {Array} [extraIds] Additional column ids to treat as money for this table.
 * @returns {Array|Object}
 */
export function filterDocumentDetailPriceColumns(columns, extraIds = []) {
    if (resolveCanReadDocumentDetailPrices(window.bo)) {
        return columns;
    }

    return dropColumns(columns, PRICE_COLUMN_IDS.concat(extraIds));
}

/**
 * Same as filterPriceColumns(), but for catalog-side datatables (the return-catalog product
 * popup, the B2B cart table), which are gated by read_prices rather than read_document_prices.
 *
 * @param {Array|Object} columns bo-datatable column definitions.
 * @param {Array} [extraIds] Additional column ids to treat as money for this table.
 * @returns {Array|Object}
 */
export function filterCatalogPriceColumns(columns, extraIds = []) {
    if (resolveCanReadPrices(window.bo)) {
        return columns;
    }

    return dropColumns(columns, PRICE_COLUMN_IDS.concat(extraIds));
}
